
// Helper: get numeric step with fallback
export const getTimeStep = (sim) => Number(sim?.timeInterval ?? 100);

export function normalizeInstructionText(value, { stripStepLabels = false } = {}) {
  let text = String(value || "");
  const trimmed = text.trim();

  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    try {
      text = JSON.parse(trimmed);
    } catch (e) {
      text = trimmed.slice(1, -1);
    }
  }

  let normalized = String(text)
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\"/g, '"');

  if (stripStepLabels) {
    normalized = normalized
      .replace(/```/g, "")
      .replace(/\(Map\)\s*/g, "")
      .replace(/\(Reduce\)\s*/g, "")
      .replace(/\(Print\)\s*/g, "")
      .replace(/\(Reduce, Print\)\s*/g, "");
  }

  return normalized.trim();
}

export function parseTaggedJsonMessage(rawMessage) {
  const raw = String(rawMessage || "").trim();
  let jsonStr = null;

  if (raw.startsWith("{") || raw.startsWith("[")) {
    try {
      return JSON.parse(raw);
    } catch (e) {
      // Fall through to marker-based parsing.
    }
  }

  const markerRegex =
    /`{0,3}output_json_start\s*([\s\S]*?)\s*`{0,3}output_json_end/;
  const match = raw.match(markerRegex);

  if (match && match[1]) {
    jsonStr = match[1].trim();
  } else {
    const fallbackRegex =
      /output_json_start\s*([\s\S]*?)\s*output_json_end/;
    const fallbackMatch = raw.match(fallbackRegex);
    if (fallbackMatch && fallbackMatch[1]) {
      jsonStr = fallbackMatch[1].trim();
    }
  }

  if (!jsonStr) return null;
  return JSON.parse(jsonStr);
}

// Helper: snap time to the nearest step so file names line up cleanly
export const alignToStep = (t, step) => {
  const s = Number(step) || 100;
  return Math.round(Number(t) / s) * s;
};



export const formatTime = (totalSeconds, { style = "long" } = {}) => {
  const s    = Math.max(0, Math.round(totalSeconds));
  const hrs  = Math.floor(s / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;

  if (style === "digital") {
    // hh:mm:ss if >= 1 hour; otherwise mm:ss
    if (hrs > 0) {
      return `${hrs}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    }
    return `${mins}:${String(secs).padStart(2, "0")}`;
  }

  // existing long/verbose style (unchanged default)
  const hrsDisplay  = hrs  > 0 ? `${hrs} hrs ` : "";
  const minsDisplay = mins > 0 ? `${mins} min ` : "";
  const secsDisplay = `${secs} secs`;
  return (hrsDisplay + minsDisplay + secsDisplay).trim();
};

// Haversine distance (in meters) between two lat/lng points.
export const haversineDistance = (lat1, lng1, lat2, lng2) => {
    const toRad = (x) => x * Math.PI / 180;
    const R = 6371000; // Earth's radius in meters
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat/2) ** 2 +
              Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
              Math.sin(dLng/2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Dijkstra algorithm to compute shortest paths.
 export const dijkstra = (adjList, start) => {
    const distances = {};
    const previous = {};
    const nodes = new Set(Object.keys(adjList));
    nodes.forEach(node => {
      distances[node] = Infinity;
      previous[node] = null;
    });
    distances[start] = 0;
    while (nodes.size > 0) {
      let minNode = null;
      nodes.forEach(node => {
        if (minNode === null || distances[node] < distances[minNode]) {
          minNode = node;
        }
      });
      if (minNode === null) break;
      nodes.delete(minNode);
      if (distances[minNode] === Infinity) break;
      for (let neighbor of adjList[minNode]) {
        let alt = distances[minNode] + neighbor.weight;
        if (alt < distances[neighbor.node]) {
          distances[neighbor.node] = alt;
          previous[neighbor.node] = { node: minNode, link: neighbor.link };
        }
      }
    }
    return { distances, previous };
  };


  // Reconstruct path from start to end using previous pointers.
export const reconstructPath = (previous, start, end) => {
    const path = [];
    let current = end;
    while (current !== start) {
      path.unshift(current);
      if (!previous[current]) break;
      current = previous[current].node;
    }
    if (current === start) {
      path.unshift(start);
    }
    return path;
  };

// Connect additional household nodes to the closest node already used by the
// primary evacuation routes. These paths are based only on graph link length;
// callers intentionally do not send them through congestion/time APIs.
export const buildFreeFlowConnectorRoutes = ({ adjList, links, primaryLinkIds, householdNodeIds }) => {
  const linkById = new Map(
    (links || []).map((link) => [String(link?.link_id ?? ""), link])
  );
  const primaryNodes = new Set();
  (primaryLinkIds || []).forEach((linkId) => {
    const link = linkById.get(String(linkId));
    if (!link) return;
    primaryNodes.add(String(link.start_node_id));
    primaryNodes.add(String(link.end_node_id));
  });

  if (!primaryNodes.size) return [];

  return (householdNodeIds || []).map((rawNodeId) => {
    const nodeId = String(rawNodeId ?? "").trim();
    if (!nodeId || !adjList?.[nodeId]) return null;

    const { distances, previous } = dijkstra(adjList, nodeId);
    let joinNodeId = null;
    let shortestDistance = Infinity;
    primaryNodes.forEach((candidate) => {
      const distance = Number(distances?.[candidate]);
      if (Number.isFinite(distance) && distance < shortestDistance) {
        shortestDistance = distance;
        joinNodeId = candidate;
      }
    });
    if (!joinNodeId) return null;

    const nodePath = reconstructPath(previous, nodeId, joinNodeId);
    if (!Array.isArray(nodePath) || nodePath[0] !== nodeId) return null;
    const linkIds = [];
    for (let index = 0; index < nodePath.length - 1; index += 1) {
      const from = String(nodePath[index]);
      const to = String(nodePath[index + 1]);
      const edge = (adjList[from] || []).find((item) => String(item?.node) === to);
      const linkId = edge?.link?.link_id;
      if (linkId == null) return null;
      linkIds.push(String(linkId));
    }
    return { nodeId, joinNodeId, nodePath, linkIds, distance: shortestDistance };
  }).filter(Boolean);
};

/* ---------- small geometry utilities ---------- */
export function bearing(lat1, lng1, lat2, lng2) {
    // initial bearing in degrees 0‑360
    const toRad = d => d * Math.PI / 180;
    const y = Math.sin(toRad(lng2 - lng1)) * Math.cos(toRad(lat2));
    const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
              Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(toRad(lng2 - lng1));
    return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
  }
  
  export function toCompass(deg) {
    const dirs = ['North', 'North‑East', 'East', 'South‑East',
                  'South', 'South‑West', 'West', 'North‑West'];
    return dirs[Math.round(deg / 45) % 8];
  }
  
  export function parseLineString(wkt) {
    // "LINESTRING (lng lat, lng lat …)"  ➜  [{lat,lng}, …]
    return wkt
      .replace(/^LINESTRING\s*\(|\)$/gi, '')
      .split(',')
      .map(pt => pt.trim().split(/\s+/).map(Number))
      .map(([lng, lat]) => ({ lat, lng }));
  }
  
  export function turnInstruction(prevBearing, nextBearing) {
    // signed smallest‑angle difference (‑180..180)
    const diff = ((nextBearing - prevBearing + 540) % 360) - 180;
    if (Math.abs(diff) < 45) return 'continue';
    return diff > 0 ? 'right' : 'left';
  }
